/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { Either } from 'purify-ts/Either';
import { Maybe } from 'purify-ts/Maybe';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import Icon from '@/components/Icon';
import Loader from '@/components/Loader';
import * as utils from '@/utils';
import './style.less';
export default tsx.componentFactoryOf().create({
    name: 'promise-loader',
    props: {
        promise: p.ofType().required,
        waitAtLeast: p(Number).default(250),
        duration: p(Number).default(750),
        scale: p(Number).default(1),
        pageLoader: p(Boolean).default(false),
        popoverBoundary: p(String).optional,
        popoverPlacement: p(String).optional,
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            state: 'loading',
            promiseIdx: 0,
            compId: `promise-loader-${utils.getUniqueId()}`,
        };
    },
    watch: {
        promise: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler() {
                this.onPromiseChange();
            },
        },
    },
    computed: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        error() {
            if (this.state instanceof Error) {
                return Maybe.of(this.state);
            }
            return Maybe.empty();
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async maybeCall(data, key) {
            for (const result of utils.ensureArray(data)) {
                if (result?.[key] != null) {
                    // eslint-disable-next-line no-await-in-loop
                    await result[key]();
                }
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderSlotOrNothing(slot) {
            return this.$scopedSlots[slot]?.() ?? utils.emptyVNode();
        },
        // Unwrap a either value. This is used to make it possible to handle a
        // normal promise an a `Promise<Either<L, R>>` in the same way.
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeUnwrapEither(value) {
            if (Either.isEither(value)) {
                return value.unsafeCoerce();
            }
            return value;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onPromiseChange() {
            this.promiseIdx++;
            this.state = 'loading';
            const savedIdx = this.promiseIdx;
            if (this.promise == null) {
                this.state = 'hidden';
                return;
            }
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const isCorrect = () => this.promiseIdx === savedIdx;
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const onCorrect = (cont) => {
                if (isCorrect()) {
                    cont();
                }
            };
            utils
                .waitAtLeast(this.waitAtLeast, this.promise)
                .then(this.maybeUnwrapEither)
                .then(response => {
                onCorrect(async () => {
                    this.state = 'success';
                    await this.maybeCall(response, 'onSuccess');
                    if (isCorrect()) {
                        tsx.emitOn(this, 'onSuccess', response);
                    }
                    setTimeout(() => {
                        onCorrect(async () => {
                            this.state = 'hidden';
                            await this.maybeCall(response, 'onAfterSuccess');
                            if (isCorrect()) {
                                tsx.emitOn(this, 'onAfterSuccess', response);
                            }
                        });
                        this.state = 'hidden';
                    }, this.duration);
                });
            }, err => {
                onCorrect(async () => {
                    this.state = err;
                    await this.maybeCall(err, 'onError');
                    if (isCorrect()) {
                        tsx.emitOn(this, 'onError', err);
                    }
                });
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async hideError(e) {
            this.promiseIdx++;
            const savedIdx = this.promiseIdx;
            e.stopPropagation();
            await this.maybeCall(this.state, 'onAfterError');
            if (savedIdx === this.promiseIdx) {
                tsx.emitOn(this, 'onAfterError', {});
            }
            this.state = 'hidden';
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderPageLoader() {
            const body = this.error.caseOf({
                Just: error => (<b-alert variant="danger" show>
                        {utils.getErrorMessage(error)}
                    </b-alert>),
                Nothing: () => [
                    <Loader page-loader scale={this.scale}/>,
                    this.renderSlotOrNothing('loading'),
                ],
            });
            return <div class="promise-loader">{body}</div>;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        inlineLoader() {
            const iconId = `${this.compId}-icon`;
            const body = this.error.caseOf({
                Just: error => [
                    <Icon id={iconId} icon={faXmark} class="text-danger"/>,
                    <b-popover show target={iconId} triggers="" variant="danger" boundary={this.popoverBoundary} placement={this.popoverPlacement}>
                        <Icon icon={faXmark} class="hide-button" onClick={e => {
                            this.hideError(e);
                        }}/>
                        {utils.getErrorMessage(error)}
                    </b-popover>,
                ],
                Nothing: () => {
                    switch (this.state) {
                        case 'loading':
                            return [
                                <Loader center={false} scale={this.scale}/>,
                                this.renderSlotOrNothing('loading'),
                            ];
                        case 'success':
                        case 'hidden':
                            return [
                                <Icon icon={faCheck} class="text-success" style={{
                                        opacity: this.state === 'success' ? 1 : 0,
                                    }}/>,
                                this.renderSlotOrNothing('success'),
                            ];
                        default:
                            return utils.emptyVNode();
                    }
                },
            });
            return <div class="promise-loader inline-loader d-inline">{body}</div>;
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render() {
        if (this.pageLoader) {
            return this.renderPageLoader();
        }
        else {
            return this.inlineLoader();
        }
    },
});
